import { Link, NavLink, useNavigate } from "react-router-dom";
import { LogOut, Settings, Lock } from "lucide-react";
import { useIdleLock } from "./LockScreen";
import { MobileMenu } from "./MobileMenu";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../context/I18nContext";

const LangToggle = () => {
  const { lang, setLang } = useI18n();
  return (
    <div className="flex rounded-full border border-[#E5C158]/25 p-0.5 text-xs font-semibold">
      {["fr", "en"].map((l) => (
        <button
          key={l}
          data-testid={`lang-toggle-${l}`}
          onClick={() => setLang(l)}
          className={`px-3 py-1 rounded-full uppercase tracking-wider transition-colors duration-200 ${lang === l ? "bg-[#E5C158] text-[#060A12]" : "text-[#8E9BB4] hover:text-[#E5C158]"}`}
        >
          {l}
        </button>
      ))}
    </div>
  );
};

const navCls = ({ isActive }) =>
  `text-sm tracking-wide transition-colors duration-200 ${isActive ? "text-[#E5C158]" : "text-[#C2C9DB] hover:text-[#E5C158]"}`;

export const Header = () => {
  const { user, logout, lock } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  useIdleLock();

  return (
    <header className="sticky top-0 z-40 backdrop-blur-xl bg-[#060A12]/75 border-b border-white/5">
      <div className="max-w-6xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between gap-4">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2">
          <span className="w-7 h-7 rounded-full border border-[#E5C158]/60 grid place-items-center">
            <span className="w-2 h-2 rounded-full bg-[#E5C158]" />
          </span>
          <span className="font-serif text-2xl text-[#F4F6FA] tracking-wide">Écho</span>
        </Link>
        <div className="flex items-center gap-3 sm:gap-5">
          {user && user.paid && (
            <nav className="hidden sm:flex items-center gap-6">
              <NavLink to="/journal" end data-testid="nav-journal-link" className={navCls}>{t("nav_journal")}</NavLink>
              <NavLink to="/calendar" data-testid="nav-calendar-link" className={navCls}>{t("nav_calendar")}</NavLink>
              <NavLink to="/record" data-testid="nav-record-link" className={navCls}>{t("nav_record")}</NavLink>
              <NavLink to="/settings" data-testid="nav-settings-link" className={navCls} title={t("nav_settings")}><Settings size={17} /></NavLink>
              {user.pin_enabled && user.pin_unlocked && (
                <button data-testid="nav-lock-btn" onClick={lock} title={t("pin_lock_now")} className="text-[#C2C9DB] hover:text-[#E5C158] transition-colors"><Lock size={17} /></button>
              )}
            </nav>
          )}
          <LangToggle />
          {user && user.paid && <MobileMenu />}
          {user ? (
            <button
              data-testid="nav-logout-btn"
              onClick={async () => { await logout(); nav("/"); }}
              className={`text-[#8E9BB4] hover:text-[#E5C158] transition-colors ${user.paid ? "hidden sm:block" : ""}`}
              title={t("nav_logout")}
            >
              <LogOut size={18} />
            </button>
          ) : (
            <div className="flex items-center gap-3">
              <Link to="/auth?mode=login" data-testid="nav-login-btn" className="hidden sm:inline text-sm text-[#C2C9DB] hover:text-[#E5C158]">{t("nav_login")}</Link>
              <Link to="/auth?mode=register" data-testid="nav-register-btn" className="btn-gold text-sm px-4 py-2 rounded-full">{t("nav_register")}</Link>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};
